import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    CreateMakeDocument,
    type CreateMakeMutation,
    type CreateMakeMutationVariables,
} from '../../../../api/mutations/createMake';
import { type LocalMakeSettings } from '../../../../api/types';
import useLoadingButton from '../../../../components/button/useLoadingButton';
import Form from '../../../../components/fields/Form';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import { defaultValues } from '../../MakeDetailsPage/MakeDetailsInnerPage';
import useMakeFormValidator from '../../MakeDetailsPage/useMakeFormValidator';
import AddMakeForm from './AddMakeForm';

type MakeFormValues = LocalMakeSettings & {
    moduleId?: string;
};

const AddMakePage = () => {
    const { t } = useTranslation('makeDetails');
    const navigate = useNavigate();
    const validator = useMakeFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();
    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: MakeFormValues) => {
            const { moduleId, ...settings } = values;
            setLoading(true);
            // submitting message
            message.loading({
                content: t('makeDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });
            await apolloClient
                .mutate<CreateMakeMutation, CreateMakeMutationVariables>({
                    mutation: CreateMakeDocument,
                    variables: { moduleId, settings },
                })
                .finally(() => {
                    // clean up
                    message.destroy('primary');
                    setLoading(false);
                });

            // show successful message
            message.success({
                content: t('makeDetails:messages.createSuccessful'),
                key: 'primary',
            });
            navigate(`/admin/vehicles/makes`);
        },
        [apolloClient, navigate, setLoading, t]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="makeDetailsForm" htmlType="submit" type="primary">
                    {t('makeDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/vehicles/makes')}
            title={t('makeDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form id="makeDetailsForm" name="makeDetailsForm" onSubmitCapture={handleSubmit}>
                        <AddMakeForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddMakePage;
